/**
 * The application store.
 *
 * One source of truth: the pseudocode. Everything else — diagnostics, the
 * graph, the outline — is derived from it by the compile pipeline. Nothing in
 * the UI is allowed to edit the graph directly, which is what guarantees the
 * code and the picture can never disagree.
 *
 * Two behaviours here are worth calling out:
 *
 *  - **The last good diagram is kept.** While the document has syntax errors
 *    the previous drawable graph stays on screen, marked stale. Blanking the
 *    canvas every time someone types an opening brace would make the tool
 *    unusable.
 *  - **Undo/redo is delegated to CodeMirror.** A second history competing with
 *    the editor's own is a reliable source of desynchronisation, so the toolbar
 *    drives the editor's history rather than keeping its own.
 */
import { create } from 'zustand';
import { BRANDING } from '../config/branding';
import type { Diagnostic } from '../language/diagnostics/types';
import type { SourceRange } from '../language/ast/source';
import type { FlowGraph } from '../graph/model/types';
import { EMPTY_GRAPH } from '../graph/model/types';
import type { AppPreferences } from '../persistence/preferences';
import { DEFAULT_PREFERENCES, loadPreferences, savePreferences } from '../persistence/preferences';
import type { ProjectRecord, ProjectStore, SaveStatus } from '../persistence';
import { createAutosave, createMemoryStore, newProjectId, nowIso } from '../persistence';
import type { AutosaveController } from '../persistence/autosave';
import { createCompilerClient } from './compiler';
import type { CompilerClient } from './compiler';
import type { PipelineOptions, PipelineResult } from './pipelineTypes';
import { getExample, PRIMARY_EXAMPLE_ID } from '../examples';

export type PanelFocus = 'editor' | 'diagram';

export interface AppState {
  /* --- document ---------------------------------------------------- */
  readonly projectId: string;
  readonly projectName: string;
  readonly source: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly dirty: boolean;

  /* --- derived ----------------------------------------------------- */
  readonly diagnostics: readonly Diagnostic[];
  readonly graph: FlowGraph;
  readonly nodeRanges: ReadonlyMap<string, SourceRange>;
  readonly functionNames: readonly string[];
  /** True while the graph on screen is older than the source. */
  readonly stale: boolean;
  readonly compiling: boolean;
  readonly lastCompileMs: number;
  readonly compileError: string | null;

  /* --- selection --------------------------------------------------- */
  readonly selectedNodeId: string | null;
  readonly selectedEdgeId: string | null;
  readonly cursorOffset: number;
  readonly focusedPanel: PanelFocus;
  /** Set when the diagram asks the editor to reveal a range. */
  readonly pendingReveal: SourceRange | null;

  /* --- preferences & persistence ------------------------------------ */
  readonly preferences: AppPreferences;
  readonly saveStatus: SaveStatus;
  readonly saveError: string | null;
  readonly storeKind: ProjectStore['kind'];

  /* --- actions ------------------------------------------------------ */
  setSource(source: string): void;
  setProjectName(name: string): void;
  newProject(source?: string, name?: string): void;
  loadProject(record: ProjectRecord): void;
  recompileNow(): void;
  setPreferences(patch: Partial<AppPreferences>): void;
  selectNode(id: string | null): void;
  selectEdge(id: string | null): void;
  setCursorOffset(offset: number): void;
  setFocusedPanel(panel: PanelFocus): void;
  /** Asks the editor to scroll to and select a range; cleared once consumed. */
  requestReveal(range: SourceRange | null): void;
  toggleFunctionExpansion(name: string): void;
  attachPersistence(store: ProjectStore): void;
  flushSave(): Promise<void>;
}

/* ------------------------------------------------------------------ *
 * Module-level singletons
 * ------------------------------------------------------------------ */

let compiler: CompilerClient | null = null;
let projectStore: ProjectStore = createMemoryStore();
let autosave: AutosaveController | null = null;

function pipelineOptions(
  preferences: AppPreferences,
  overrides: ReadonlyMap<string, 'expanded' | 'collapsed'>,
): PipelineOptions {
  return {
    direction: preferences.direction,
    functionDisplay: preferences.functionDisplay,
    functionOverrides: [...overrides.entries()],
    yesLabel: preferences.yesLabel,
    noLabel: preferences.noLabel,
    terminatorLabelStyle: 'full-phrase',
    showUnusedFunctions: true,
    nodeSpacing: preferences.nodeSpacing,
    rankSpacing: preferences.rankSpacing,
  };
}

const functionOverrides = new Map<string, 'expanded' | 'collapsed'>();

function initialSource(): string {
  return getExample(PRIMARY_EXAMPLE_ID)?.source ?? 'Start app;\nEnd the diagram;\n';
}

export const useAppStore = create<AppState>()((set, get) => {
  const preferences = loadPreferences();

  /** Applies a compile result, dropping it if the source moved on. */
  const applyResult = (result: PipelineResult): void => {
    const state = get();
    const current = state.source;

    set({
      compiling: false,
      lastCompileMs: result.durationMs,
      compileError: null,
      diagnostics: result.diagnostics,
      functionNames: result.functionNames,
      // A result for older text still carries useful diagnostics, but its graph
      // is not what the user is looking at.
      stale: result.source !== current,
    });

    // Keep the last drawable graph when the new one would be empty because of
    // syntax errors — an empty canvas mid-edit is worse than a stale one.
    const worthShowing = result.graph.nodes.length > 0 || !result.hasErrors;
    if (result.source === current && worthShowing) {
      set({
        graph: result.graph,
        nodeRanges: new Map(result.nodeRanges),
        stale: false,
      });
    } else if (result.source === current && !worthShowing) {
      set({ stale: true });
    }
  };

  const ensureCompiler = (): CompilerClient => {
    if (compiler) return compiler;
    compiler = createCompilerClient(preferences.debounceMs);
    compiler.onResult(applyResult);
    compiler.onError((message) => set({ compiling: false, compileError: message }));
    return compiler;
  };

  const scheduleCompile = (source: string, immediate = false): void => {
    const client = ensureCompiler();
    const options = pipelineOptions(get().preferences, functionOverrides);
    set({ compiling: true });
    if (immediate) client.requestNow(source, options);
    else client.request(source, options);
  };

  const scheduleSave = (): void => {
    const state = get();
    if (!autosave) return;
    autosave.schedule({
      id: state.projectId,
      name: state.projectName,
      source: state.source,
      createdAt: state.createdAt,
      updatedAt: nowIso(),
      settings: {
        direction: state.preferences.direction,
        expandedFunctions: [...functionOverrides.entries()]
          .filter(([, mode]) => mode === 'expanded')
          .map(([name]) => name),
        functionDisplay: state.preferences.functionDisplay,
        yesLabel: state.preferences.yesLabel,
        noLabel: state.preferences.noLabel,
      },
    });
  };

  const source = initialSource();
  const timestamp = nowIso();

  // The first compile runs immediately so the app never flashes an empty canvas.
  queueMicrotask(() => scheduleCompile(source, true));

  return {
    projectId: newProjectId(),
    projectName: `Untitled ${BRANDING.name.toLowerCase()}`,
    source,
    createdAt: timestamp,
    updatedAt: timestamp,
    dirty: false,

    diagnostics: [],
    graph: EMPTY_GRAPH,
    nodeRanges: new Map(),
    functionNames: [],
    stale: false,
    compiling: true,
    lastCompileMs: 0,
    compileError: null,

    selectedNodeId: null,
    selectedEdgeId: null,
    cursorOffset: 0,
    focusedPanel: 'editor',
    pendingReveal: null,

    preferences,
    saveStatus: 'idle',
    saveError: null,
    storeKind: projectStore.kind,

    setSource(next) {
      if (next === get().source) return;
      set({ source: next, dirty: true, updatedAt: nowIso() });
      if (get().preferences.autoUpdate) scheduleCompile(next);
      else set({ stale: true });
      scheduleSave();
    },

    setProjectName(name) {
      set({ projectName: name.slice(0, 200), dirty: true, updatedAt: nowIso() });
      scheduleSave();
    },

    newProject(nextSource = '', name) {
      const now = nowIso();
      const id = newProjectId();
      set({
        projectId: id,
        projectName: name ?? `Untitled ${BRANDING.name.toLowerCase()}`,
        source: nextSource,
        createdAt: now,
        updatedAt: now,
        dirty: false,
        selectedNodeId: null,
        selectedEdgeId: null,
      });
      functionOverrides.clear();
      const next = { ...get().preferences, lastProjectId: id };
      set({ preferences: next });
      savePreferences(next);
      scheduleCompile(nextSource, true);
      scheduleSave();
    },

    loadProject(record) {
      set({
        projectId: record.id,
        projectName: record.name,
        source: record.source,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        dirty: false,
        selectedNodeId: null,
        selectedEdgeId: null,
      });
      functionOverrides.clear();
      for (const name of record.settings.expandedFunctions) {
        functionOverrides.set(name.toLowerCase(), 'expanded');
      }
      set({
        preferences: {
          ...get().preferences,
          direction: record.settings.direction,
          functionDisplay: record.settings.functionDisplay,
          yesLabel: record.settings.yesLabel,
          noLabel: record.settings.noLabel,
          lastProjectId: record.id,
        },
      });
      savePreferences(get().preferences);
      scheduleCompile(record.source, true);
    },

    recompileNow() {
      scheduleCompile(get().source, true);
    },

    setPreferences(patch) {
      const next = { ...get().preferences, ...patch };
      set({ preferences: next });
      savePreferences(next);
      compiler?.setDebounce(next.debounceMs);

      // Anything that changes the shape of the diagram needs a recompile.
      const structural: (keyof AppPreferences)[] = [
        'direction',
        'functionDisplay',
        'yesLabel',
        'noLabel',
        'nodeSpacing',
        'rankSpacing',
      ];
      if (structural.some((key) => key in patch)) {
        scheduleCompile(get().source, true);
      } else if ('autoUpdate' in patch && patch.autoUpdate && get().stale) {
        scheduleCompile(get().source, true);
      }
    },

    selectNode(id) {
      set({ selectedNodeId: id, selectedEdgeId: id ? null : get().selectedEdgeId });
    },

    selectEdge(id) {
      set({ selectedEdgeId: id, selectedNodeId: id ? null : get().selectedNodeId });
    },

    setCursorOffset(offset) {
      set({ cursorOffset: offset });

      // Reverse mapping: the innermost node whose range contains the caret.
      const { nodeRanges } = get();
      let best: { id: string; span: number } | null = null;
      for (const [id, range] of nodeRanges) {
        if (offset < range.start.offset || offset > range.end.offset) continue;
        const span = range.end.offset - range.start.offset;
        if (!best || span < best.span) best = { id, span };
      }
      if (best && best.id !== get().selectedNodeId) {
        set({ selectedNodeId: best.id, selectedEdgeId: null });
      }
    },

    setFocusedPanel(panel) {
      set({ focusedPanel: panel });
    },

    requestReveal(range) {
      set({ pendingReveal: range });
    },

    toggleFunctionExpansion(name) {
      const key = name.toLowerCase();
      const fallback = get().preferences.functionDisplay;
      const current = functionOverrides.get(key) ?? fallback;
      const next = current === 'expanded' ? 'collapsed' : 'expanded';
      if (next === fallback) functionOverrides.delete(key);
      else functionOverrides.set(key, next);
      scheduleCompile(get().source, true);
      scheduleSave();
    },

    attachPersistence(store) {
      projectStore = store;
      autosave?.dispose();
      autosave = createAutosave(store, 800);
      autosave.subscribe((status, error) =>
        set({ saveStatus: status, saveError: error?.message ?? null }),
      );
      set({ storeKind: store.kind });

      // Reopen whatever was being edited last. Until storage is attached the
      // app shows the starter example, so this is the first moment a restore is
      // even possible.
      const lastId = get().preferences.lastProjectId;
      if (lastId) {
        void store
          .get(lastId)
          .then((record) => {
            if (record) get().loadProject(record);
            else scheduleSave();
          })
          .catch(() => scheduleSave());
        return;
      }
      // Nothing to restore: register the current document so it is not lost.
      scheduleSave();
      const next = { ...get().preferences, lastProjectId: get().projectId };
      set({ preferences: next });
      savePreferences(next);
    },

    async flushSave() {
      await autosave?.flush();
    },
  };
});

/** Test seam: lets a test drive the store without a real IndexedDB. */
export function __resetAppStoreSingletons(): void {
  compiler?.dispose();
  compiler = null;
  autosave?.dispose();
  autosave = null;
  projectStore = createMemoryStore();
  functionOverrides.clear();
}

export function getProjectStore(): ProjectStore {
  return projectStore;
}

export const DEFAULT_APP_PREFERENCES = DEFAULT_PREFERENCES;
